/**
 * Episode detail module
 * Handles the episode detail view
 *
 * NOTE: This module now delegates to episode-view.js for the full-page experience.
 * The modal-based view is kept for backward compatibility but is no longer the primary view.
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml, stripHtml } from '../core/utils.js';
import { formatTime, seekToTime, toggleTranscriptionFormat, copyTranscription, setupAudioTimeTracking } from './audio-player.js';
import { openEpisodeView } from './episode-view.js';

/**
 * View episode details - now opens full-page view
 */
export async function viewEpisodeDetails(episodeId) {
    // Use the new full-page view
    openEpisodeView(episodeId);
}

/**
 * View episode details in modal (legacy - kept for backward compatibility)
 */
export async function viewEpisodeDetailsModal(episodeId) {
    try {
        const episode = await API.get(`/api/episodes/${episodeId}`);

        // Store episode data globally for copy function
        window.currentEpisodeData = episode;

        const modal = document.getElementById('episode-modal');
        const details = document.getElementById('episode-details');

        // Format transcription content
        let transcriptionContent = '';
        let hasSpeakerData = false;

        if (episode.transcription_data && episode.transcription_data.segments_with_speakers) {
            hasSpeakerData = true;
            transcriptionContent = formatSpeakerTranscription(episode);
        } else if (episode.transcription) {
            transcriptionContent = formatRawTranscription(episode);
        }

        // Create audio player if file is available
        let audioPlayer = '';
        if (episode.local_file_path && episode.downloaded) {
            const audioUrl = `/api/episodes/${episodeId}/audio`;
            audioPlayer = `
                <div style="margin-bottom: 1.5rem; background: var(--bg-dark); padding: 1rem; border-radius: 8px;">
                    <h3 style="margin-bottom: 0.5rem;">🎧 Audio Player</h3>
                    <audio controls style="width: 100%;" id="episode-audio">
                        <source src="${audioUrl}" type="audio/mpeg">
                        Your browser does not support the audio element.
                    </audio>
                    <p style="margin-top: 0.5rem; color: var(--text-secondary); font-size: 0.85rem;">
                        💡 Shortcuts: Space = Play/Pause | ← = -10s | → = +10s | Esc = Close
                    </p>
                </div>
            `;
        } else if (episode.audio_url) {
            audioPlayer = `
                <div style="margin-bottom: 1.5rem; background: var(--bg-dark); padding: 1rem; border-radius: 8px;">
                    <h3 style="margin-bottom: 0.5rem;">🎧 Audio Player (Stream)</h3>
                    <audio controls style="width: 100%;" id="episode-audio">
                        <source src="${episode.audio_url}" type="audio/mpeg">
                        Your browser does not support the audio element.
                    </audio>
                </div>
            `;
        }

        // Build modal content
        details.innerHTML = `
            <button class="btn btn-secondary" onclick="closeModal()" style="margin-bottom: 1.5rem;">← Close</button>
            <h2>${episode.title}</h2>
            <p style="color: var(--text-secondary); margin-bottom: 1.5rem;">
                ${episode.published_date ? new Date(episode.published_date).toLocaleDateString() : ''}
                ${episode.duration_seconds ? ` • ${Math.floor(episode.duration_seconds / 60)} min` : ''}
            </p>

            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.5rem;">
                ${episode.downloaded ? '<span class="badge downloaded">Downloaded</span>' : '<span class="badge pending">Not Downloaded</span>'}
                ${episode.processed ? '<span class="badge processed">Processed</span>' : ''}
                ${hasSpeakerData ? '<span class="badge" style="background: var(--accent);">Speaker Diarization</span>' : ''}
            </div>

            ${episode.description ? `
                <div style="margin-bottom: 1.5rem; color: var(--text-secondary);">
                    ${escapeHtml(stripHtml(episode.description))}
                </div>
            ` : ''}

            ${audioPlayer}

            <h3 style="margin-bottom: 1rem;">📝 Transcription</h3>
            ${transcriptionContent || '<p style="color: var(--text-secondary);">No transcription available. Use "Fetch Transcription" to retrieve it from the server.</p>'}

            <div style="margin-top: 2rem; display: flex; gap: 0.5rem; flex-wrap: wrap;">
                ${!episode.downloaded ? `<button class="btn" onclick="downloadEpisode(${episode.id})">📥 Download</button>` : ''}
                <button class="btn" onclick="fetchTranscription(${episode.id}, ${episode.transcription ? 'true' : 'false'})"
                        style="background: linear-gradient(135deg, #9b59b6, #8e44ad);">
                    📝 ${episode.transcription ? 'Re-fetch' : 'Fetch'} Transcription
                </button>
                ${episode.downloaded ? `
                    <button class="btn" onclick="redoDiarization(${episode.id})"
                            style="background: linear-gradient(135deg, #e74c3c, #c0392b);">
                        🎤 Add/Redo Diarization
                    </button>
                ` : ''}
            </div>
        `;

        modal.classList.add('active');

        // Setup audio time tracking for segment highlighting
        setTimeout(() => {
            setupAudioTimeTracking();
        }, 100);

    } catch (error) {
        console.error('Error viewing episode details:', error);
        showNotification('Failed to load episode details', 'error');
    }
}

/**
 * Format speaker-diarized transcription
 */
function formatSpeakerTranscription(episode) {
    const segments = episode.transcription_data.segments_with_speakers;

    // Group consecutive segments by speaker
    const speakerSegments = [];
    let currentSpeaker = null;
    let currentTexts = [];
    let segmentStart = null;
    let lastTimestamp = 0;

    segments.forEach((segment) => {
        if (segment.speaker !== currentSpeaker) {
            if (currentSpeaker !== null && currentTexts.length > 0) {
                speakerSegments.push({
                    speaker: currentSpeaker,
                    text: currentTexts.join(' '),
                    start: segmentStart,
                    end: segment.start || null
                });
            }
            currentSpeaker = segment.speaker;
            currentTexts = [segment.text];
            segmentStart = segment.start || null;
        } else {
            currentTexts.push(segment.text);
        }

        if (segment.end) {
            lastTimestamp = segment.end;
        }
    });

    // Add last segment
    if (currentSpeaker !== null && currentTexts.length > 0) {
        speakerSegments.push({
            speaker: currentSpeaker,
            text: currentTexts.join(' '),
            start: segmentStart,
            end: lastTimestamp || null
        });
    }

    return `
        <div style="background: var(--bg-dark); padding: 1.5rem; border-radius: 8px;">
            <div style="margin-bottom: 1rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
                <button class="btn btn-small" onclick="toggleTranscriptionFormat()" id="format-toggle-btn"
                        style="background: linear-gradient(135deg, #3498db, #2980b9);">
                    📄 Show Raw Text
                </button>
                <button class="btn btn-small btn-secondary" onclick="copyTranscription()">
                    📋 Copy
                </button>
            </div>
            <div style="max-height: 500px; overflow-y: auto; padding: 0.5rem;">
                <div id="transcription-diarized">
                    ${speakerSegments.map((seg, index) => {
                        const showTimestamp = seg.start && (index === 0 ||
                            (speakerSegments[index-1] && speakerSegments[index-1].start &&
                             seg.start - speakerSegments[index-1].start > 60));

                        return `
                            ${showTimestamp ? `
                                <div style="text-align: center; margin: 1rem 0; color: var(--text-secondary); font-size: 0.85rem;">
                                    <span style="background: var(--bg-dark); padding: 0.25rem 0.75rem; border-radius: 12px; cursor: pointer;"
                                          onclick="seekToTime(${seg.start})" title="Click to jump to this time">
                                        ⏱️ ${formatTime(seg.start)}
                                    </span>
                                </div>
                            ` : ''}
                            <div class="speaker-segment"
                                 data-start="${seg.start || 0}"
                                 data-end="${seg.end || 999999}"
                                 style="margin-bottom: 1rem; padding: 0.75rem; background: var(--bg-card); border-radius: 6px; border-left: 3px solid var(--accent-light); transition: all 0.3s;">
                                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem;">
                                    <span style="color: var(--accent-light); font-weight: 600; font-size: 0.9rem;">
                                        ${seg.speaker}
                                    </span>
                                    ${seg.start ? `
                                        <span style="color: var(--text-secondary); font-size: 0.8rem; cursor: pointer; padding: 0.2rem 0.5rem; border-radius: 4px; background: var(--bg-dark);"
                                              onclick="seekToTime(${seg.start})"
                                              title="Jump to ${formatTime(seg.start)}">
                                            ▶ ${formatTime(seg.start)}
                                        </span>
                                    ` : ''}
                                </div>
                                <div style="color: var(--text); line-height: 1.6;">
                                    ${seg.text}
                                </div>
                            </div>
                        `;
                    }).join('')}
                </div>
                <div id="transcription-raw" style="display: none; color: var(--text); line-height: 1.6; white-space: pre-wrap; font-family: monospace; font-size: 0.9rem;">
                    ${episode.transcription}
                </div>
            </div>
        </div>
    `;
}

/**
 * Format raw transcription (no speaker diarization)
 */
function formatRawTranscription(episode) {
    return `
        <div style="background: var(--bg-dark); padding: 1.5rem; border-radius: 8px;">
            <div style="margin-bottom: 1rem; display: flex; gap: 0.5rem; flex-wrap: wrap;">
                <button class="btn btn-small btn-secondary" onclick="copyTranscription()">
                    📋 Copy Transcription
                </button>
            </div>
            <div style="max-height: 500px; overflow-y: auto; padding: 0.5rem;">
                <div id="transcription-raw" style="color: var(--text); line-height: 1.6; white-space: pre-wrap;">
                    ${episode.transcription}
                </div>
            </div>
        </div>
    `;
}
